import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useRouterState } from "@tanstack/react-router";
import {
  CalendarDays,
  CalendarSearch,
  Check,
  ChevronDown,
  ChevronUp,
  Contact,
  Copy,
  MessageCircle,
  Plus,
  ListCheck,
  Star,
  type LucideIcon,
} from "lucide-react";
import {
  type ComponentType,
  type UIEvent,
  useLayoutEffect,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { SidebarUserSettingsMenu } from "@/components/navigation/SidebarUserSettingsMenu";
import {
  formatFollowingAccessibleLabel,
  getThreadSidebarNavigation,
} from "@/components/navigation/threadNavigation";
import { STUDYGROUP_VISIBILITY_SEARCH_DISABLED } from "@/constants";
import { useCopyLinkToast } from "@/features/study-group-files/hooks/useCopyLinkToast";
import { useStudyGroupUnreadBadgeCounts } from "@/features/study-group-channels/hooks/useStudyGroupUnreadBadgeCounts";
import { useMyStudyGroupsPaginated } from "@/hooks/useMyStudyGroupsPaginated";
import { useStudyGroupNavigation } from "@/hooks/useStudyGroupNavigation";
import { navigationOrderApi } from "@/lib/humhub-api/navigationOrder";
import { chatsApi } from "@/lib/humhub-api/chats";
import {
  type CatchUpItem,
  STREAM_ACTIVITIES_READ_EVENT,
  streamReadApi,
} from "@/lib/humhub-api/streamRead";
import type { LightStudyGroupResponse } from "@/lib/humhub-api/types";
import { chatKeys } from "@/lib/queryKeys/chatKeys";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";
import {
  countOffscreenUnreadGroups,
  resolveStudyGroupUnreadTotals,
  toStudyGroupUnreadTotalsFromCatchUp,
} from "@/lib/studyGroupUnread";
import { copyPublicStudyGroupLinkToClipboard } from "@/lib/utils/studyGroupLinks";
import { useGlobalUserInfo } from "@/providers/AuthProvider";
import { useThreadUnreadSummary } from "@/features/threads";

const navigationOrderKey = ["navigation-order", "study-groups"] as const;
const STUDY_GROUP_LIST_SCROLL_TOP_KEY =
  "rulewatcher:sidebar:study-group-list-scroll-top";
const getStudyGroupListScrollTopKey = (userId?: number | null) =>
  userId != null
    ? `${STUDY_GROUP_LIST_SCROLL_TOP_KEY}:${userId}`
    : STUDY_GROUP_LIST_SCROLL_TOP_KEY;

const orderGroups = (
  groups: LightStudyGroupResponse[],
  orderedIds: readonly number[] | undefined,
) => {
  // The study-group endpoint is ordered by last visit. Never use that order in
  // the sidebar: before the persisted order is available, keep a stable order.
  if (!orderedIds) return [...groups].sort((left, right) => left.id - right.id);
  const byId = new Map(groups.map((group) => [group.id, group]));
  const result = orderedIds.flatMap((id) => {
    const group = byId.get(id);
    if (!group) return [];
    byId.delete(id);
    return [group];
  });
  // Groups joined after the saved order was loaded are new sidebar entries.
  // Append them instead of allowing the last-visit API order to move them.
  const newGroups = [...byId.values()].sort(
    (left, right) => left.id - right.id,
  );
  return [...result, ...newGroups];
};

const StaticNavItem = ({
  to,
  label,
  icon: Icon,
  active,
  unreadCount = 0,
  mentionCount = 0,
  notificationLabel,
  ariaLabel,
}: {
  to: string;
  label: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number | string;
  }>;
  active: boolean;
  unreadCount?: number;
  mentionCount?: number;
  notificationLabel?: string;
  ariaLabel?: string;
}) => {
  const [tooltipTop, setTooltipTop] = useState<number | null>(null);
  const showTooltip = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setTooltipTop(rect.top + rect.height / 2);
  };

  return (
    <>
      <Link
        to={to}
        aria-label={
          ariaLabel ??
          (notificationLabel ? `${label}: ${notificationLabel}` : label)
        }
        aria-current={active ? "page" : undefined}
        onMouseEnter={(event) => showTooltip(event.currentTarget)}
        onMouseLeave={() => setTooltipTop(null)}
        onFocus={(event) => showTooltip(event.currentTarget)}
        onBlur={() => setTooltipTop(null)}
        className={`group relative flex size-9 items-center justify-center rounded-full border transition-[background-color,box-shadow,color,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a505e] focus-visible:ring-offset-2 focus-visible:ring-offset-[#e8eff1] ${
          active
            ? "border-[#0a505e] bg-[#0a505e] text-white shadow-md ring-2 ring-[#0a505e]/15"
            : "border-white/90 bg-white/75 text-[#0a505e] shadow-sm hover:-translate-y-0.5 hover:border-white hover:bg-white hover:shadow-md"
        }`}
      >
        <Icon className="relative z-10 size-5" strokeWidth={2.2} />
        {mentionCount > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 z-20 min-w-4 rounded-full bg-rose-500 px-1 text-center text-[10px] font-bold leading-4 text-white ring-2 ring-[#e8eff1]"
          >
            {mentionCount > 99 ? "99+" : mentionCount}
          </span>
        ) : unreadCount > 0 ? (
          <span
            aria-hidden="true"
            className="absolute right-0 top-0 z-20 size-2.5 rounded-full bg-sky-500 ring-2 ring-[#e8eff1]"
          />
        ) : null}
      </Link>
      {tooltipTop !== null && typeof document !== "undefined"
        ? createPortal(
            <div
              className="pointer-events-none fixed left-[70px] z-[100] -translate-y-1/2 whitespace-nowrap rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#424f53] shadow-xl ring-1 ring-slate-200 [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:py-1.5 [@media(max-height:700px)]:text-xs"
              style={{ top: tooltipTop }}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const BottomNavItem = ({
  to,
  label,
  icon: Icon,
  active = false,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
}) => (
  <Link
    to={to}
    aria-label={label}
    aria-current={active ? "page" : undefined}
    className="group relative flex size-10 shrink-0 [@media(max-height:700px)]:size-9"
  >
    <span
      className={`absolute left-0 top-0 z-20 flex size-10 items-center overflow-hidden rounded-r-full shadow-sm transition-[width] duration-200 ease-out group-hover:w-[190px] group-focus-visible:w-[190px] [@media(max-height:700px)]:size-9 ${
        active
          ? "bg-rw-main text-white group-hover:bg-rw-main group-focus-visible:bg-rw-main"
          : "bg-white text-slate-600"
      }`}
    >
      <span className="flex size-10 shrink-0 items-center justify-center [@media(max-height:700px)]:size-9">
        <Icon
          className="size-5 [@media(max-height:700px)]:size-[18px]"
          strokeWidth={2.2}
        />
      </span>
      <span className="whitespace-nowrap pr-5 text-sm font-semibold [@media(max-height:700px)]:text-xs">
        {label}
      </span>
    </span>
  </Link>
);

export const Sidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const user = useGlobalUserInfo();
  const studyGroupListScrollTopKey = useMemo(
    () => getStudyGroupListScrollTopKey(user?.id),
    [user?.id],
  );
  const queryClient = useQueryClient();
  const unreadRefreshTimerRef = useRef<number | null>(null);
  const { navigateToStudyGroup } = useStudyGroupNavigation();
  const copyWithToast = useCopyLinkToast({ showSuccessToast: false });
  const { accumulatedGroups, isLoading } = useMyStudyGroupsPaginated(100);

  useEffect(() => {
    const handleActivitiesRead = () => {
      if (unreadRefreshTimerRef.current !== null) {
        window.clearTimeout(unreadRefreshTimerRef.current);
      }
      unreadRefreshTimerRef.current = window.setTimeout(() => {
        unreadRefreshTimerRef.current = null;
        void queryClient.invalidateQueries({
          queryKey: studyGroupKeys.myStudyGroupsRoot,
        });
      }, 150);
    };

    window.addEventListener(STREAM_ACTIVITIES_READ_EVENT, handleActivitiesRead);
    return () => {
      window.removeEventListener(
        STREAM_ACTIVITIES_READ_EVENT,
        handleActivitiesRead,
      );
      if (unreadRefreshTimerRef.current !== null) {
        window.clearTimeout(unreadRefreshTimerRef.current);
      }
    };
  }, [queryClient]);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    id: number;
    position: "before" | "after";
  } | null>(null);
  const dragScrollFrameRef = useRef<number | null>(null);
  const dragPointerYRef = useRef(0);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<{
    group: LightStudyGroupResponse;
    top: number;
  } | null>(null);
  const [allStudyGroupsTooltipTop, setAllStudyGroupsTooltipTop] = useState<
    number | null
  >(null);
  const hoverCloseTimer = useRef<number | null>(null);
  const groupListRef = useRef<HTMLDivElement>(null);
  const groupItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const groupListScrollSaveFrameRef = useRef<number | null>(null);
  const pendingGroupListScrollSaveRef = useRef<number | null>(null);
  const pendingGroupListScrollRestoreRef = useRef<number | null>(null);
  const [canScrollGroupsUp, setCanScrollGroupsUp] = useState(false);
  const [canScrollGroupsDown, setCanScrollGroupsDown] = useState(false);
  const [offscreenUnreadCounts, setOffscreenUnreadCounts] = useState({
    above: 0,
    below: 0,
  });
  const routerMatches = useRouterState({ select: (state) => state.matches });
  const activeStudyGroupId = routerMatches
    .map(
      (match) =>
        (match.params as Record<string, string | undefined>)["study-group-id"],
    )
    .find(Boolean);
  const activeStudyGroupNumericId = Number(activeStudyGroupId);
  const { data: activeStudyGroupUnreadTotals } = useStudyGroupUnreadBadgeCounts(
    {
      studyGroupId: activeStudyGroupNumericId,
    },
  );
  const { data: activeStudyGroupCatchUpItems = [] } = useQuery<CatchUpItem[]>({
    queryKey: ["stream-catch-up", activeStudyGroupNumericId, true],
    queryFn: () =>
      streamReadApi.getCatchUp(activeStudyGroupNumericId, {
        includeComments: true,
        limit: 100,
      }),
    enabled:
      Number.isSafeInteger(activeStudyGroupNumericId) &&
      activeStudyGroupNumericId > 0,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const activeStudyGroupCatchUpUnreadTotals = useMemo(() => {
    const currentUserId = Number(user?.id);
    const visibleItems = activeStudyGroupCatchUpItems.filter(
      (item) =>
        !Number.isSafeInteger(currentUserId) ||
        item.createdBy !== currentUserId,
    );
    return toStudyGroupUnreadTotalsFromCatchUp(visibleItems);
  }, [activeStudyGroupCatchUpItems, user?.id]);

  const { data: orderedIds } = useQuery({
    queryKey: navigationOrderKey,
    queryFn: navigationOrderApi.getStudyGroups,
    staleTime: 5 * 60 * 1000,
  });
  const { data: chatUnreadTotals } = useQuery({
    queryKey: chatKeys.unreadTotalsAll(),
    queryFn: () => chatsApi.getChatUnreadCounts(),
    staleTime: 60 * 1000,
  });
  const { data: threadUnreadSummary } = useThreadUnreadSummary();
  const threadMentionCount = threadUnreadSummary?.unread_mention_count ?? 0;
  const threadUnreadCount = threadUnreadSummary?.unread_post_comment_count ?? 0;
  const threadNavigation = getThreadSidebarNavigation(
    threadUnreadCount,
    threadMentionCount,
  );
  const favoritesLabel = t("threads.tabs.favorites");
  const followingLabel = t("threads.tabs.following");
  const followingAccessibleLabel = threadNavigation.following.hasUnread
    ? formatFollowingAccessibleLabel(followingLabel, t("threads.unread"))
    : undefined;
  const orderedGroups = useMemo(
    () => orderGroups(accumulatedGroups, orderedIds),
    [accumulatedGroups, orderedIds],
  );
  const getGroupUnreadTotals = useCallback(
    (group: LightStudyGroupResponse) =>
      resolveStudyGroupUnreadTotals(
        group,
        activeStudyGroupNumericId === group.id,
        activeStudyGroupUnreadTotals,
        activeStudyGroupCatchUpUnreadTotals,
      ),
    [
      activeStudyGroupCatchUpUnreadTotals,
      activeStudyGroupNumericId,
      activeStudyGroupUnreadTotals,
    ],
  );

  const readGroupListScrollTop = useCallback(() => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = window.localStorage.getItem(studyGroupListScrollTopKey);
      const fallback = user?.id
        ? window.localStorage.getItem(STUDY_GROUP_LIST_SCROLL_TOP_KEY)
        : null;
      const parsed = Number(raw ?? fallback);
      if (!Number.isFinite(parsed) || parsed < 0) return 0;
      return Math.floor(parsed);
    } catch {
      return 0;
    }
  }, [studyGroupListScrollTopKey, user?.id]);

  const storeGroupListScrollTop = useCallback(
    (scrollTop: number) => {
      if (typeof window === "undefined") return;
      try {
        const normalized = Math.max(0, Math.floor(scrollTop));
        window.localStorage.setItem(
          studyGroupListScrollTopKey,
          String(normalized),
        );
      } catch {
        // Ignore storage write errors
      }
    },
    [studyGroupListScrollTopKey],
  );

  const updateGroupScrollIndicators = useCallback(() => {
    const element = groupListRef.current;
    if (!element) return;
    setCanScrollGroupsUp(element.scrollTop > 1);
    setCanScrollGroupsDown(
      element.scrollTop + element.clientHeight < element.scrollHeight - 1,
    );
  }, []);

  const updateOffscreenUnreadIndicators = useCallback(() => {
    const container = groupListRef.current;
    if (!container || isLoading) {
      setOffscreenUnreadCounts((current) =>
        current.above === 0 && current.below === 0
          ? current
          : { above: 0, below: 0 },
      );
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const counts = countOffscreenUnreadGroups(
      orderedGroups.flatMap((group) => {
        const element = groupItemRefs.current.get(group.id);
        if (!element) return [];
        const rect = element.getBoundingClientRect();
        const totals = getGroupUnreadTotals(group);
        return [
          {
            hasUnread:
              totals.unreadPostCommentCount > 0 || totals.unreadMentions > 0,
            top: rect.top,
            bottom: rect.bottom,
          },
        ];
      }),
      { top: containerRect.top, bottom: containerRect.bottom },
    );
    setOffscreenUnreadCounts((current) =>
      current.above === counts.above && current.below === counts.below
        ? current
        : counts,
    );
  }, [getGroupUnreadTotals, isLoading, orderedGroups]);

  const applyPendingGroupListScrollRestore = useCallback(() => {
    const element = groupListRef.current;
    const scrollTop = pendingGroupListScrollRestoreRef.current;
    if (!element || scrollTop === null) return false;
    const maxScrollTop = Math.max(
      0,
      element.scrollHeight - element.clientHeight,
    );
    element.scrollTop = Math.min(scrollTop, maxScrollTop);
    pendingGroupListScrollRestoreRef.current = null;
    updateGroupScrollIndicators();
    return true;
  }, [updateGroupScrollIndicators]);

  const flushGroupListScrollSave = useCallback(() => {
    if (groupListScrollSaveFrameRef.current !== null) {
      cancelAnimationFrame(groupListScrollSaveFrameRef.current);
      groupListScrollSaveFrameRef.current = null;
    }
    const pending = pendingGroupListScrollSaveRef.current;
    pendingGroupListScrollSaveRef.current = null;
    if (pending !== null) {
      storeGroupListScrollTop(pending);
    }
  }, [storeGroupListScrollTop]);

  const scheduleGroupListScrollSave = useCallback(
    (scrollTop: number) => {
      pendingGroupListScrollSaveRef.current = scrollTop;
      if (groupListScrollSaveFrameRef.current !== null) return;
      groupListScrollSaveFrameRef.current = requestAnimationFrame(() => {
        groupListScrollSaveFrameRef.current = null;
        const pending = pendingGroupListScrollSaveRef.current;
        pendingGroupListScrollSaveRef.current = null;
        if (pending !== null) {
          storeGroupListScrollTop(pending);
        }
      });
    },
    [storeGroupListScrollTop],
  );

  const onGroupListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      updateGroupScrollIndicators();
      updateOffscreenUnreadIndicators();
      scheduleGroupListScrollSave(event.currentTarget.scrollTop);
    },
    [
      scheduleGroupListScrollSave,
      updateGroupScrollIndicators,
      updateOffscreenUnreadIndicators,
    ],
  );

  useLayoutEffect(() => {
    if (pendingGroupListScrollRestoreRef.current === null) {
      pendingGroupListScrollRestoreRef.current = readGroupListScrollTop();
    }
    if (isLoading || orderedGroups.length === 0) return;
    const frame = requestAnimationFrame(() => {
      applyPendingGroupListScrollRestore();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [
    applyPendingGroupListScrollRestore,
    isLoading,
    orderedGroups.length,
    readGroupListScrollTop,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushGroupListScrollSave();
      }
    };
    const handlePageHide = () => {
      flushGroupListScrollSave();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      flushGroupListScrollSave();
    };
  }, [flushGroupListScrollSave]);

  useEffect(() => {
    updateGroupScrollIndicators();
    updateOffscreenUnreadIndicators();
    const element = groupListRef.current;
    if (!element) return;
    const handleResize = () => {
      updateGroupScrollIndicators();
      updateOffscreenUnreadIndicators();
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(element);
    if (element.firstElementChild) {
      resizeObserver.observe(element.firstElementChild);
    }
    return () => resizeObserver.disconnect();
  }, [updateGroupScrollIndicators, updateOffscreenUnreadIndicators]);
  const saveOrder = useMutation({
    mutationFn: navigationOrderApi.saveStudyGroups,
    onSuccess: (ids) => queryClient.setQueryData(navigationOrderKey, ids),
  });

  const stopGroupDragAutoScroll = useCallback(() => {
    if (dragScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }
  }, []);

  const startGroupDragAutoScroll = useCallback(
    (clientY: number) => {
      dragPointerYRef.current = clientY;
      if (dragScrollFrameRef.current !== null) return;

      const scroll = () => {
        const element = groupListRef.current;
        if (!element) {
          dragScrollFrameRef.current = null;
          return;
        }
        const rect = element.getBoundingClientRect();
        const edgeSize = 80;
        const pointerY = dragPointerYRef.current;
        let delta = 0;
        if (pointerY < rect.top + edgeSize) {
          delta = -Math.ceil(
            ((rect.top + edgeSize - pointerY) / edgeSize) * 10,
          );
        } else if (pointerY > rect.bottom - edgeSize) {
          delta = Math.ceil(
            ((pointerY - (rect.bottom - edgeSize)) / edgeSize) * 10,
          );
        }
        if (delta !== 0) {
          element.scrollTop += delta;
          scheduleGroupListScrollSave(element.scrollTop);
          updateGroupScrollIndicators();
        }
        dragScrollFrameRef.current = window.requestAnimationFrame(scroll);
      };

      dragScrollFrameRef.current = window.requestAnimationFrame(scroll);
    },
    [scheduleGroupListScrollSave, updateGroupScrollIndicators],
  );

  useEffect(() => stopGroupDragAutoScroll, [stopGroupDragAutoScroll]);

  const moveGroup = (targetId: number, position: "before" | "after") => {
    if (draggedId === null || draggedId === targetId) return;
    const ids = orderedGroups.map((group) => group.id);
    const from = ids.indexOf(draggedId);
    if (from < 0) return;
    const [movedId] = ids.splice(from, 1);
    const targetIndex = ids.indexOf(targetId);
    if (targetIndex < 0) return;
    ids.splice(targetIndex + (position === "after" ? 1 : 0), 0, movedId);
    queryClient.setQueryData(navigationOrderKey, ids);
    saveOrder.mutate(ids);
    setDraggedId(null);
    setDropTarget(null);
  };

  const copyGroupLink = async (group: LightStudyGroupResponse) => {
    const copied = await copyWithToast(() =>
      copyPublicStudyGroupLinkToClipboard(group.guid),
    );
    if (copied) {
      setCopiedId(group.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    }
  };

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  const scheduleHoverClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = window.setTimeout(() => {
      setHoveredGroup(null);
      setAllStudyGroupsTooltipTop(null);
      hoverCloseTimer.current = null;
    }, 120);
  };

  return (
    <aside className="flex h-full w-16 flex-col items-center border-r border-slate-200 bg-[#e8eff1] py-3 [@media(max-height:700px)]:py-1.5">
      <nav className="flex shrink-0 flex-col items-center gap-1.5 [@media(max-height:700px)]:gap-1">
        <StaticNavItem
          to="/chat-room"
          label={t("common.chat")}
          icon={MessageCircle}
          active={pathname.startsWith("/chat-room")}
          unreadCount={chatUnreadTotals?.unreadMessages}
          mentionCount={chatUnreadTotals?.unreadMentions}
        />
        <StaticNavItem
          to={threadNavigation.favorites.to}
          label={favoritesLabel}
          icon={Star}
          active={pathname.startsWith("/favorites")}
        />
        <StaticNavItem
          to={threadNavigation.following.to}
          label={followingLabel}
          icon={ListCheck}
          active={pathname.startsWith("/following")}
          unreadCount={threadUnreadCount}
          mentionCount={threadMentionCount}
          ariaLabel={followingAccessibleLabel}
        />
      </nav>

      <div className="my-2 h-px w-8 shrink-0 bg-slate-300 [@media(max-height:700px)]:my-1" />

      <div
        className="relative min-h-0 w-full flex-1"
        onDragOver={(event) => {
          if (draggedId === null) return;
          event.preventDefault();
          startGroupDragAutoScroll(event.clientY);
        }}
        onDragLeave={(event) => {
          const nextTarget = event.relatedTarget;
          if (
            nextTarget instanceof Node &&
            event.currentTarget.contains(nextTarget)
          ) {
            return;
          }
          stopGroupDragAutoScroll();
          setDropTarget(null);
        }}
      >
        <div
          ref={groupListRef}
          onScroll={onGroupListScroll}
          className="h-full overflow-y-auto overflow-x-visible px-3 pb-16 pt-2 [scrollbar-width:none] [@media(max-height:700px)]:pb-14 [@media(max-height:700px)]:pt-1.5"
        >
          <div className="flex flex-col items-center gap-2 pb-2 [@media(max-height:700px)]:gap-1.5 [@media(max-height:700px)]:pb-1.5">
            {isLoading ? (
              <span className="size-7 animate-pulse rounded-full bg-slate-300" />
            ) : (
              orderedGroups.map((group) => {
                const unreadTotals = getGroupUnreadTotals(group);
                const mentions = unreadTotals.unreadMentions;
                const hasUnread = unreadTotals.unreadPostCommentCount > 0;
                return (
                  <div
                    key={group.id}
                    ref={(element) => {
                      if (element) groupItemRefs.current.set(group.id, element);
                      else groupItemRefs.current.delete(group.id);
                    }}
                    draggable
                    onDragStart={() => {
                      setDraggedId(group.id);
                      setDropTarget(null);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      startGroupDragAutoScroll(event.clientY);
                      if (draggedId === group.id) {
                        setDropTarget(null);
                        return;
                      }
                      const rect = event.currentTarget.getBoundingClientRect();
                      setDropTarget({
                        id: group.id,
                        position:
                          event.clientY < rect.top + rect.height / 2
                            ? "before"
                            : "after",
                      });
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      stopGroupDragAutoScroll();
                      if (dropTarget?.id === group.id) {
                        moveGroup(group.id, dropTarget.position);
                      }
                    }}
                    onDragEnd={() => {
                      stopGroupDragAutoScroll();
                      setDraggedId(null);
                      setDropTarget(null);
                    }}
                    onMouseEnter={(event) => {
                      cancelHoverClose();
                      setAllStudyGroupsTooltipTop(null);
                      const rect = event.currentTarget.getBoundingClientRect();
                      setHoveredGroup({
                        group,
                        top: rect.top + rect.height / 2,
                      });
                    }}
                    onMouseLeave={scheduleHoverClose}
                    className="group relative"
                  >
                    {dropTarget?.id === group.id ? (
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute -inset-x-1 z-30 h-0.5 rounded-full bg-rw-main shadow-sm ${
                          dropTarget.position === "before"
                            ? "-top-1"
                            : "-bottom-1"
                        }`}
                      />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => navigateToStudyGroup(group.id)}
                      aria-label={group.name}
                      className={`relative flex size-10 items-center justify-center rounded-full transition-[background-color,box-shadow,opacity] [@media(max-height:700px)]:size-9 ${
                        activeStudyGroupId === String(group.id)
                          ? "bg-rw-main/15 shadow-sm ring ring-rw-main ring-offset-2 ring-offset-white"
                          : "hover:ring-rw-main/30 hover:ring-2"
                      }`}
                    >
                      <span className="flex size-10 items-center justify-center [@media(max-height:700px)]:scale-90">
                        <CustomAvatar
                          name={group.name}
                          imageUrl={group.image_url}
                          size={40}
                          roundedFull
                        />
                      </span>
                      {mentions > 0 ? (
                        <span className="absolute -right-1 -top-1 z-20 min-w-4 rounded-full bg-rose-500 px-1 text-center text-[10px] font-bold leading-4 text-white ring-2 ring-[#e8eff1]">
                          {mentions > 99 ? "99+" : mentions}
                        </span>
                      ) : hasUnread ? (
                        <span className="absolute right-0 top-0 z-20 size-2.5 rounded-full bg-sky-500 ring-2 ring-[#e8eff1]" />
                      ) : null}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
        {canScrollGroupsUp ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-20 bg-gradient-to-b from-[#e8eff1] to-transparent" />
        ) : null}
        {offscreenUnreadCounts.above > 0 ? (
          <div className="pointer-events-none absolute inset-x-0 top-1 z-40 flex justify-center">
            <span className="sr-only">
              {t("common.unreadGroupsOffscreenAbove")}
            </span>
            <span
              aria-hidden="true"
              className="flex items-center gap-1 text-sky-600"
            >
              <ChevronUp className="size-4" strokeWidth={2.5} />
              <span className="size-2 rounded-full bg-sky-500" />
            </span>
          </div>
        ) : null}
        {canScrollGroupsDown ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-14 z-20 h-20 bg-gradient-to-t from-[#e8eff1] to-transparent [@media(max-height:700px)]:bottom-12" />
        ) : null}
        {offscreenUnreadCounts.below > 0 ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-16 z-40 flex justify-center [@media(max-height:700px)]:bottom-14">
            <span className="sr-only">
              {t("common.unreadGroupsOffscreenBelow")}
            </span>
            <span
              aria-hidden="true"
              className="flex items-center gap-1 text-sky-600"
            >
              <ChevronDown className="size-4" strokeWidth={2.5} />
              <span className="size-2 rounded-full bg-sky-500" />
            </span>
          </div>
        ) : null}
        <div
          className={`absolute inset-x-0 bottom-0 z-30 flex h-14 items-center justify-center bg-[#e8eff1] [@media(max-height:700px)]:h-12 ${
            pathname.startsWith("/all-study-groups")
              ? "[&_a:hover]:!bg-rw-main [&_a:hover]:!text-white [&_a]:border-solid [&_a]:border-rw-main [&_a]:bg-rw-main [&_a]:text-white [&_a]:shadow-sm"
              : ""
          }`}
        >
          <Link
            to="/all-study-groups"
            aria-label={t("common.allStudyGroups")}
            aria-current={
              pathname.startsWith("/all-study-groups") ? "page" : undefined
            }
            onMouseEnter={(event) => {
              cancelHoverClose();
              setHoveredGroup(null);
              const rect = event.currentTarget.getBoundingClientRect();
              setAllStudyGroupsTooltipTop(rect.top + rect.height / 2);
            }}
            onMouseLeave={scheduleHoverClose}
            onFocus={(event) => {
              setHoveredGroup(null);
              const rect = event.currentTarget.getBoundingClientRect();
              setAllStudyGroupsTooltipTop(rect.top + rect.height / 2);
            }}
            onBlur={scheduleHoverClose}
            className={`flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-rw-main transition-colors [@media(max-height:700px)]:size-9 ${
              pathname.startsWith("/all-study-groups")
                ? "border-solid bg-rw-main text-white shadow-sm hover:bg-rw-main hover:text-white"
                : "border-dashed text-rw-main hover:bg-white"
            }`}
          >
            <Plus className="size-5 [@media(max-height:700px)]:size-[18px]" />
          </Link>
        </div>
      </div>

      <nav className="mt-auto flex w-full shrink-0 flex-col items-start gap-1.5 [@media(max-height:700px)]:gap-0.5">
        <BottomNavItem
          to="/calendar"
          label={t("common.myCalendar")}
          icon={CalendarDays}
          active={pathname.startsWith("/calendar")}
        />
        <BottomNavItem
          to="/event-search"
          label={t("common.eventSearch")}
          icon={CalendarSearch}
          active={pathname.startsWith("/event-search")}
        />
        {user?.id ? (
          <BottomNavItem
            to={`/user-profile/${user.id}`}
            label={t("common.myPage")}
            icon={Contact}
            active={pathname.startsWith(`/user-profile/${user.id}`)}
          />
        ) : null}
        <SidebarUserSettingsMenu isCollapsed variant="global-sidebar" />
      </nav>
      {hoveredGroup && typeof document !== "undefined"
        ? createPortal(
            <div
              onMouseEnter={cancelHoverClose}
              onMouseLeave={scheduleHoverClose}
              className="fixed left-[70px] z-[100] flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-lg bg-white px-3 py-2 shadow-xl ring-1 ring-slate-200"
              style={{ top: hoveredGroup.top }}
            >
              <span className="max-w-56 truncate text-sm font-semibold text-slate-700 [@media(max-height:700px)]:text-xs">
                {hoveredGroup.group.name}
              </span>
              {!hoveredGroup.group.archived &&
              hoveredGroup.group.visibility !==
                STUDYGROUP_VISIBILITY_SEARCH_DISABLED ? (
                <button
                  type="button"
                  onClick={() => void copyGroupLink(hoveredGroup.group)}
                  aria-label={`Copy link to ${hoveredGroup.group.name}`}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-rw-main"
                >
                  {copiedId === hoveredGroup.group.id ? (
                    <Check className="size-4" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
      {allStudyGroupsTooltipTop !== null && typeof document !== "undefined"
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed left-[70px] z-[100] -translate-y-1/2 whitespace-nowrap rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-xl ring-1 ring-slate-200 [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:py-1.5 [@media(max-height:700px)]:text-xs"
              style={{ top: allStudyGroupsTooltipTop }}
            >
              {t("common.allStudyGroups")}
            </div>,
            document.body,
          )
        : null}
    </aside>
  );
};
